/**
 * EL PAPEL DE UNA COTIZACIÓN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ NO ES UN RECIBO CON OTRO TÍTULO, Y ESA ERA LA TENTACIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Reutilizar `componerTicket` cambiando la palabra «RECIBO» por «COTIZACIÓN»
 * habría costado diez minutos y habría salido un papel que MIENTE:
 *
 *   · Un recibo lleva **FORMA DE PAGO**. Aquí nadie pagó nada. Un bloque que
 *     diga «Efectivo $12,00» en un presupuesto es un cliente convencido de que
 *     ya pagó.
 *   · Un recibo dice «¡Gracias por su compra!». No hubo compra.
 *   · Un recibo no caduca. Un presupuesto **tiene** que caducar, o el cliente
 *     aparece en noviembre exigiendo el precio de marzo.
 *   · Un recibo no lleva a quién va dirigido. Un presupuesto sí: es lo que se
 *     compara con el de los otros dos que pidió.
 *
 * Así que la composición es propia. Lo que SÍ se reutiliza es la maquinaria de
 * imprimir —`ArmadorDeTicket`, los 58/80 mm, los márgenes de seguridad, el PDF
 * con su tamaño dentro— porque eso costó dos fallos reales aprenderlo y no hay
 * ningún motivo para repetirlos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  UNA SOLA COMPOSICIÓN, TRES SALIDAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Igual que el recibo: se escribe una vez sobre un `ArmadorDeTicket` y sale por
 * donde haga falta —bytes a la térmica, PDF del ancho del rollo, o la vista de
 * pantalla—. Con dos composiciones, el día que se toque una línea la otra se
 * queda vieja y nadie se entera hasta que alguien compara dos papeles.
 */

import { Ticket, recortar, type AnchoPapel, type PaginaDeCodigos } from "@/lib/escpos";
import type { ImagenDeTicket } from "@/lib/imagenTicket";
import { formatMoney, toNumber, type FormatoMoneda, monedaImpresa } from "@/lib/money";
import { reciboComoPdf } from "@/lib/reciboPdf";
import { TicketEnLineas } from "@/lib/ticketLineas";
import { contactoDelPapel, enNegrita, type ArmadorDeTicket } from "@/features/pos/ticketEscpos";
import { llevaMarcaDeExento, nombresDeLosExentos } from "@/lib/exentos";
import type { Cotizacion, Tenant } from "@/lib/types";

export type OpcionesCotizacion = {
  ancho: AnchoPapel;
  pagina: PaginaDeCodigos;
  cortarPapel: boolean;
  monedas?: FormatoMoneda[];
  logo?: ImagenDeTicket | null;
};

const GENERICA: FormatoMoneda = { code: "", symbol: "", decimals: 2, symbol_before: false };

/**
 * La entrega en el papel: la del autoservicio y, desde el 2026-11-02, la de
 * la página web. Sin tildes, como el resto del ticket. ⚠️ Antes todo lo que
 * no era «llevar» salía «Comer aqui», y un delivery se habría impreso así.
 */
const ENTREGA_EN_EL_PAPEL: Record<string, string> = {
  aqui: "Comer aqui",
  llevar: "Para llevar",
  retiro: "Retiro en el local",
  delivery: "Delivery",
};

function formatoDe(codigo: string, monedas: FormatoMoneda[] | undefined): FormatoMoneda {
  return monedas?.find((m) => m.code === codigo) ?? GENERICA;
}

/** Solo el número: en la columna de importes la moneda ya se sobreentiende. */
function cifra(valor: string, codigo: string, monedas: FormatoMoneda[] | undefined): string {
  return formatMoney(valor, formatoDe(codigo, monedas), { sinSimbolo: true });
}

/** "12,50 USD" — para el TOTAL, donde la moneda sí hace falta decirla. */
function monto(valor: string, codigo: string, monedas: FormatoMoneda[] | undefined): string {
  return `${formatMoney(valor, formatoDe(codigo, monedas), { sinSimbolo: true })} ${monedaImpresa(codigo)}`;
}

/** "2" en vez de "2.000", "1,5" en vez de "1.500". */
function cantidad(qty: string): string {
  const n = toNumber(qty);
  return Number.isInteger(n) ? String(n) : n.toLocaleString("es-VE", { maximumFractionDigits: 3 });
}

function fechaHora(iso: string): string {
  return new Date(iso).toLocaleString("es-VE", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Una fecha suelta (`2026-09-21`) en día/mes/año.
 *
 * ⚠️ Se parte a mano y NO con `new Date("2026-09-21")`. Esa cadena la lee el
 * navegador como medianoche **en UTC**, y en Venezuela (UTC−4) medianoche UTC
 * es todavía el día anterior: la fecha impresa saldría un día antes de la que
 * dice el sistema. En el papel que protege del cambio de precios, un día de
 * menos es una discusión con el cliente.
 */
/**
 * Si este papel es un PEDIDO y no un presupuesto (2026-10-15): lo que llegó del
 * autoservicio, o cualquier pedido recibido de un restaurante. Un pedido se
 * cobra en el momento: no tiene «válida hasta» ni el aviso de cotización.
 */
export function esUnPedido(
  cot: Pick<Cotizacion, "origen">,
  negocio: Pick<Tenant, "settings"> | undefined,
): boolean {
  return cot.origen === "autoservicio" || negocio?.settings?.modo_operacion === "restaurante";
}

export function soloFecha(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}

/**
 * La cotización, escrita una sola vez sobre el armador que le den.
 */
export function componerCotizacion(
  t: ArmadorDeTicket,
  cot: Cotizacion,
  negocio: Tenant | undefined,
  op: OpcionesCotizacion,
): void {
  // Todo en negrita, como el recibo. Ver `enNegrita`.
  t = enNegrita(t);
  const mon = op.monedas;
  const ancho = t.anchoLinea;
  const pedido = esUnPedido(cot, negocio);

  t.iniciar();

  // ── Quién la emite ───────────────────────────────────────────────────────
  if (op.logo) {
    t.centrar().imagen(op.logo.ancho, op.logo.alto, op.logo.bits).salto();
  }

  t.centrar().negrita().doble();
  t.parrafo(negocio?.name ?? "SOFTFI");
  t.normal().centrar();

  if (negocio?.legal_name) t.parrafo(negocio.legal_name);
  if (negocio?.tax_id) t.linea(negocio.tax_id);
  // La dirección y el teléfono del local, con las mismas reglas que el recibo.
  const { direccion, telefono } = contactoDelPapel(cot, negocio);
  if (direccion) t.parrafo(direccion);
  if (telefono) t.linea(`Tel: ${telefono}`);

  t.salto();

  /*
    ⚠️ EL TÍTULO, GRANDE Y CENTRADO. ES LA LÍNEA MÁS IMPORTANTE DEL PAPEL.

    Un presupuesto y una factura se parecen demasiado: mismo membrete, misma
    lista de productos, mismo total abajo. Si no se distinguen de un vistazo, el
    papel acaba en la carpeta equivocada — y entonces al cliente le reclaman un
    pago que nunca se le cobró, o el contador declara una venta que no existió.

    Por eso va a doble tamaño y entre rayas, antes que cualquier otra cosa.
  */
  t.izquierda().separador("=");
  t.centrar().negrita().doble();
  t.linea(pedido ? "PEDIDO" : "COTIZACION");
  t.normal().izquierda();
  t.separador("=");

  // ── De qué documento se trata ────────────────────────────────────────────
  t.negrita().columnas("N.", cot.numero).negrita(false);
  t.columnas("Fecha", fechaHora(cot.created_at));

  /*
    ⚠️ «VALIDA HASTA» EN NEGRITA, Y NO ES UN ADORNO LEGAL.

    Es lo único que protege al negocio de la inflación. Sin esta línea, el
    presupuesto es una promesa eterna: el cliente que aparece en noviembre con
    el papel de marzo tiene razón en exigir ese precio, y la tiene por escrito.
  */
  if (cot.valida_hasta && !pedido) {
    t.negrita().columnas("Valida hasta", soloFecha(cot.valida_hasta)).negrita(false);
  }
  if (cot.contacto) t.columnas("Nombre", recortar(cot.contacto, ancho - 10));
  const entrega = cot.modalidad ? ENTREGA_EN_EL_PAPEL[cot.modalidad] : undefined;
  if (entrega) t.columnas("Entrega", entrega);
  if (cot.creada_por_nombre) {
    t.columnas("Asesor", recortar(cot.creada_por_nombre, ancho - 10));
  }

  // ── Para quién ───────────────────────────────────────────────────────────
  //
  // Va en su propio bloque y no como una línea más de arriba: un presupuesto se
  // compara con el de otros dos, y lo primero que se busca al recogerlo del
  // montón es si es el suyo.
  if (cot.customer_name) {
    t.separador();
    t.negrita().linea("PARA").negrita(false);
    t.parrafo(cot.customer_name);
    if (cot.customer_tax_id) t.linea(cot.customer_tax_id);
    if (cot.customer_phone) t.linea(cot.customer_phone);
  }

  // ── Lo que se cotiza ─────────────────────────────────────────────────────
  t.separador();
  t.negrita().linea("DETALLE").negrita(false);

  // «(E)» igual que en el ticket de la venta: exento de un impuesto cotizado.
  const cotizados = cot.impuestos.map((x) => x.tax_id);
  const exentosDe = nombresDeLosExentos(cot.items, cot.impuestos);

  for (const l of cot.items) {
    const marca = llevaMarcaDeExento(l.exento_de, cotizados) ? " (E)" : "";
    const nombre = (l.variant_name ? `${l.name} (${l.variant_name})` : l.name) + marca;
    t.negrita().parrafo(nombre).negrita(false);
    // Los extras debajo del plato: el precio ya los lleva sumados.
    for (const o of l.opciones ?? []) t.parrafo(`  + ${o.nombre}`);
    t.columnas(
      `  ${cantidad(l.qty)} x ${cifra(l.unit_price, cot.currency_code, mon)}`,
      cifra(l.line_total, cot.currency_code, mon),
    );
    if (toNumber(l.discount) > 0) {
      t.columnas("  Descuento", `-${cifra(l.discount, cot.currency_code, mon)}`);
    }
  }


  t.separador();

  // ── La cuenta ────────────────────────────────────────────────────────────
  //
  // Como se hace a mano: subtotal, lo que se resta, lo que se suma, total. El
  // subtotal solo si hay algo entre él y el total; si no, sería el mismo número
  // dos veces con dos nombres.
  const sumados = cot.impuestos.filter((x) => x.suma_al_total);
  const avisados = cot.impuestos.filter((x) => !x.suma_al_total);

  const hayDescuento = toNumber(cot.discount_total) > 0;
  const sumaDePedido = sumados.reduce((acc, x) => acc + toNumber(x.monto), 0);
  const impuestoDeProductos = toNumber(cot.tax_total) - sumaDePedido;

  if (hayDescuento || toNumber(cot.tax_total) > 0) {
    t.columnas("Subtotal", cifra(cot.subtotal, cot.currency_code, mon));
  }
  if (hayDescuento) {
    t.columnas("Descuento", `-${cifra(cot.discount_total, cot.currency_code, mon)}`);
  }
  if (impuestoDeProductos > 0) {
    t.columnas("Impuesto", cifra(String(impuestoDeProductos), cot.currency_code, mon));
  }
  for (const x of sumados) {
    // La base en su propia línea cuando hay algo exento: ver el ticket de venta.
    if (exentosDe.includes(x.name)) {
      t.columnas(
        recortar(`Base ${x.name}`, ancho - 12),
        cifra(x.base_amount, cot.currency_code, mon),
      );
    }
    t.columnas(
      recortar(`${x.name} ${porcentaje(x.rate)}%`, ancho - 12),
      cifra(x.monto, cot.currency_code, mon),
    );
  }

  // ── El total ─────────────────────────────────────────────────────────────
  t.separador("=");
  t.negrita().doble();
  const totalConMoneda = monto(cot.total, cot.currency_code, mon);
  if ("TOTAL".length + 1 + totalConMoneda.length <= t.anchoLinea) {
    t.columnas("TOTAL", totalConMoneda);
    t.normal();
  } else {
    // A doble tamaño solo hay la mitad de columnas. `columnas` no avisa:
    // recorta la etiqueta y sale «TOTA». Se mide antes.
    t.columnas("TOTAL", cifra(cot.total, cot.currency_code, mon));
    t.normal();
    t.derecha().linea(monedaImpresa(cot.currency_code)).izquierda();
  }
  t.separador("=");

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LO QUE SE SUMA SOLO SI PAGA EN DIVISAS
    ═══════════════════════════════════════════════════════════════════════════

    El IGTF. Va DEBAJO del total y separado, nunca dentro, porque todavía no se
    sabe cómo va a pagar: meterlo arriba encarecería el presupuesto un 3 %
    frente a quien no lo mete, por un impuesto que quizá no se le cobre.

    Y tampoco se puede callar: el cliente vuelve, paga en dólares, y el total de
    la caja no es el del papel. Se dice con todas sus letras y con el número
    hecho, para que no haya sorpresa en ninguna de las dos direcciones.
  */
  if (avisados.length > 0) {
    t.salto();
    for (const x of avisados) {
      const monedas = (x.currency_codes ?? []).map(monedaImpresa).join("/");
      t.parrafo(monedas ? `Si paga en ${monedas} se suma:` : "Ademas se suma:");
      t.columnas(
        recortar(`  ${x.name} ${porcentaje(x.rate)}%`, ancho - 12),
        cifra(x.monto, cot.currency_code, mon),
      );
      const conImpuesto = (toNumber(cot.total) + toNumber(x.monto)).toFixed(4);
      t.negrita().columnas("  Total con este", cifra(conImpuesto, cot.currency_code, mon));
      t.negrita(false);
    }
  }

  // ── Condiciones y nota ───────────────────────────────────────────────────
  if (cot.nota) {
    t.separador();
    t.parrafo(cot.nota);
  }

  /*
    ⚠️ EL AVISO DE QUE ESTO NO ES UNA FACTURA, ESCRITO.

    El título de arriba lo dice, pero este papel se guarda meses y pasa por
    manos que no lo vieron emitir. Una frase al pie cuesta dos líneas de rollo y
    evita la única confusión cara que puede producir este documento: que alguien
    lo tome por un cobro hecho.
  */
  t.separador();
  t.centrar();
  if (pedido) {
    t.parrafo("Pedido pendiente de pago. Se cancela en la caja.");
  } else {
    t.parrafo("Esto es una cotizacion, no una factura.");
    if (cot.valida_hasta) {
      t.parrafo(`Los precios valen hasta el ${soloFecha(cot.valida_hasta)}.`);
    }
    t.parrafo("Sujeta a disponibilidad.");
  }

  t.salto();
  t.negrita().parrafo("Gracias por preferirnos");
  t.negrita(false).salto();
  t.linea("softfi.online");

  // Tres saltos antes de cortar: la cuchilla está unos centímetros por encima
  // del cabezal, y sin este avance el corte se come las últimas líneas.
  t.avanzar(3);
  if (op.cortarPapel) t.cortar();
}

/**
 * «16» a partir de «0.1600».
 *
 * El servidor guarda la tasa como fracción; el papel la enseña como porcentaje,
 * que es como la nombra todo el mundo. Se recortan los ceros de la derecha para
 * que un 3 % no salga como «3,00 %».
 */
function porcentaje(tasa: string): string {
  const n = toNumber(tasa) * 100;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "").replace(".", ",");
}

/** La cotización en bytes, para la impresora térmica. */
export function construirCotizacion(
  cot: Cotizacion,
  negocio: Tenant | undefined,
  op: OpcionesCotizacion,
): Uint8Array {
  const t = new Ticket(op.ancho, op.pagina);
  componerCotizacion(t, cot, negocio, op);
  return t.bytes();
}

/**
 * La cotización como PDF, del tamaño del rollo.
 *
 * Mismo camino que el recibo, y por el mismo motivo: el PDF lleva su geometría
 * dentro, así que se imprime con el driver que Windows puso solo y sale del
 * ancho del papel. Sin Zadig y sin sacar la impresora de la lista del sistema.
 */
export function cotizacionComoPdf(
  cot: Cotizacion,
  negocio: Tenant | undefined,
  op: OpcionesCotizacion,
): Blob {
  const t = new TicketEnLineas(op.ancho);
  componerCotizacion(t, cot, negocio, op);
  return reciboComoPdf(t.resultado(), op.ancho, t.columnasDelPapel);
}
